import { useState } from 'react'

import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import {
  BulkCreateProjectManagementPlannedRecordDocument,
  BulkCreateProjectManagementPlannedRecordMutationVariables,
  ContributorsScheduleDocument,
  DeleteProjectManagementPlannedRecordDocument,
  ProjectForSelectFragment,
  SubprojectForSelectFragment,
  UpdateProjectManagementPlannedRecordDocument,
} from 'generated/resources/graphql'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DeleteConfirmationModal from './DeleteConfirmationModal'
import { ProjectHoursAssignationForm } from './ProjectHoursAssignationForm'

export interface Props {
  open: boolean
  onClose: () => void
  onDateChange: (startDate: string) => void
  id?: number
  dailyMinutes?: number
  startDate?: string
  endDate?: string
  projectWorkerIds?: number[]
  project?: ProjectForSelectFragment
  subproject?: SubprojectForSelectFragment
  employee?: EmployeeForSelectFragmentNoAccess | null
}

export const ScheduleSidepanel = ({
  open,
  onClose,
  onDateChange,
  id,
  dailyMinutes,
  startDate,
  endDate,
  projectWorkerIds,
  project,
  subproject,
  employee,
}: Props) => {
  const defaultData = {
    dailyMinutes: dailyMinutes ?? 0,
    endDate: endDate ? dateBackendToUI(endDate) : '',
    projectWorkerIds: projectWorkerIds ?? [],
    startDate: startDate ? dateBackendToUI(startDate) : '',
    project: project,
    subproject: subproject,
    employee: employee,
  }

  const [data, setData] =
    useState<BulkCreateProjectManagementPlannedRecordMutationVariables>(defaultData)
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false)

  const handleDelete = () => {
    onSubmitDelete()
    setShowDeleteConfirmationModal(false)
  }

  const validPlannedRecord = () => {
    return !!(
      data?.projectWorkerIds?.length &&
      data?.startDate &&
      data?.endDate &&
      data?.dailyMinutes
    )
  }

  const [bulkCreatePlannedRecords, { loading: createLoading }] = useMutation(
    BulkCreateProjectManagementPlannedRecordDocument,
    {
      extractorFn: (responseData) =>
        responseData.projectManagementMutations.bulkCreateProjectmanagementPlannedRecord,
    }
  )

  const [deletePlannedRecord, { loading: deleteLoading }] = useMutation(
    DeleteProjectManagementPlannedRecordDocument,
    {
      extractorFn: (responseData) =>
        responseData.projectManagementMutations.deleteProjectmanagementPlannedRecord,
    }
  )

  const [updatePlannedRecord, { loading: updateLoading }] = useMutation(
    UpdateProjectManagementPlannedRecordDocument,
    {
      extractorFn: (responseData) =>
        responseData.projectManagementMutations.updateProjectmanagementPlannedRecord,
    }
  )

  const onChange = (value: BulkCreateProjectManagementPlannedRecordMutationVariables | null) => {
    if (value) {
      if (value.startDate !== data?.startDate) {
        onDateChange(value.startDate)
      }

      setData(value)
    }
  }

  const onSubmitCreate = async () => {
    track(trackingEvents.projectManagement.schedule.clickOnSaveInModal, {})
    const result = await bulkCreatePlannedRecords({
      variables: {
        dailyMinutes: data.dailyMinutes,
        endDate: dateUIToBackend(data.endDate),
        projectWorkerIds: data.projectWorkerIds,
        startDate: dateUIToBackend(data.startDate),
        subprojectId: data.subprojectId,
      },
      awaitRefetchQueries: true,
      refetchQueries: [ContributorsScheduleDocument],
    })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })
      onClose()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
        time: 2000,
      })
    }
  }

  const onSubmitUpdate = async () => {
    track(trackingEvents.projectManagement.schedule.clickOnUpdateInModal, {})
    if (!id) return
    const result = await updatePlannedRecord({
      variables: {
        id: id,
        dailyMinutes: data.dailyMinutes,
        endDate: dateUIToBackend(data.endDate),
        startDate: dateUIToBackend(data.startDate),
        projectWorkerId: data.projectWorkerIds[0],
      },
      awaitRefetchQueries: true,
      refetchQueries: [ContributorsScheduleDocument],
    })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })
      onClose()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
        time: 2000,
      })
    }
  }

  const onSubmitDelete = async () => {
    track(trackingEvents.projectManagement.schedule.clickOnDeleteInModal, {})
    if (!id) return
    const result = await deletePlannedRecord({
      variables: {
        id: id,
      },
      awaitRefetchQueries: true,
      refetchQueries: [ContributorsScheduleDocument],
    })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })
      onClose()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
        time: 2000,
      })
    }
  }

  const loading = createLoading || deleteLoading || updateLoading
  const isDisabled = !validPlannedRecord() || loading

  const handleClose = () => {
    track(trackingEvents.projectManagement.schedule.clickOnCancelInModal, {})
    onClose()
  }

  return (
    <>
      <Sidepanel
        width="narrow"
        title={i18n.t('project_management.schedule.sidepanel.assign_hours')}
        open={open}
        onClose={handleClose}
        closeLabel={i18n.t('common.close')}
        footer={
          <>
            <Button onClick={onClose} type="secondary">
              {i18n.t('common.cancel')}
            </Button>
            {!id ? (
              <Button disabled={isDisabled} onClick={onSubmitCreate} type="primary">
                {i18n.t('common.save')}
              </Button>
            ) : (
              <>
                <Button
                  disabled={isDisabled}
                  onClick={() => setShowDeleteConfirmationModal(true)}
                  type="secondary"
                >
                  {i18n.t('common.delete')}
                </Button>
                <Button disabled={isDisabled} onClick={onSubmitUpdate} type="primary">
                  {i18n.t('common.update')}
                </Button>
              </>
            )}
          </>
        }
      >
        <ProjectHoursAssignationForm
          plannedRecord={data}
          onChange={onChange}
          existingId={id}
          project={project}
          subproject={subproject}
          employee={employee}
        />
        {loading && (
          <Box height="full" justifyContent="center" alignItems="center">
            <Spinner />
          </Box>
        )}
      </Sidepanel>
      {showDeleteConfirmationModal && (
        <DeleteConfirmationModal
          onClose={() => setShowDeleteConfirmationModal(false)}
          onSubmit={handleDelete}
        />
      )}
    </>
  )
}
